import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, form.password);
      setDone(true);
      toast.success("Password reset — you can now sign in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <BrandMark />
        </div>

        {!token ? (
          <div data-testid="reset-invalid" className="text-center space-y-4">
            <h2 className="font-display text-2xl font-bold">Reset link missing</h2>
            <p className="text-muted-foreground">This page needs a valid reset link. Please request a new one from the sign-in screen.</p>
            <Button data-testid="reset-back-btn" onClick={() => navigate("/login")} className="rounded-full bg-primary hover:bg-primary/90 text-white">Back to sign in</Button>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-display text-2xl font-bold">Password updated</h2>
            <p className="text-muted-foreground">You can now sign in with your new password.</p>
            <Button data-testid="reset-signin-btn" onClick={() => navigate("/login")} className="rounded-full bg-primary hover:bg-primary/90 text-white">Go to sign in</Button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-3xl font-bold tracking-tight">Set a new password</h2>
            <p className="text-muted-foreground mt-1">Choose a strong password you'll remember.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label>New password</Label>
                <div className="relative">
                  <Input data-testid="reset-password" type={showPw ? "text" : "password"} required minLength={6}
                    value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" className="pr-11" />
                  <button type="button" data-testid="reset-toggle-password" onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm password</Label>
                <Input data-testid="reset-confirm" type={showPw ? "text" : "password"} required minLength={6}
                  value={form.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="••••••••" />
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-primary/90 text-white h-11">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
            <button data-testid="reset-back-link" onClick={() => navigate("/login")} className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
